import React, { useEffect, useState, useMemo } from 'react';
import { db } from '@archicore/shared';
import { collection, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { 
  Inbox, Search, Filter, Mail, Phone, Building2, 
  UserCheck, UserX, Clock, Sparkles, CheckCircle2, 
  ExternalLink, UserPlus, RefreshCw, MessageSquare, Trash2, AlertTriangle
} from 'lucide-react';

export interface SalesLead {
  id: string;
  fullName: string;
  email: string;
  company: string;
  employeesRange?: string;
  message?: string;
  phone?: string;
  role?: string;
  country?: string;
  createdAt?: Timestamp | any;
  status?: 'new' | 'contacted' | 'converted' | 'discarded';
  notes?: string;
}

interface SalesLeadsInboxProps {
  onProvisionRealClient: (leadData: { company: string; email: string; suggestedPlan: string; leadId: string }) => void;
}

export default function SalesLeadsInbox({ onProvisionRealClient }: SalesLeadsInboxProps) {
  const [leads, setLeads] = useState<SalesLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<SalesLead | null>(null);
  const [deletingLead, setDeletingLead] = useState<SalesLead | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'sales_leads'));
      const items: SalesLead[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as SalesLead));

      // Sort by createdAt desc
      items.sort((a, b) => {
        const tA = a.createdAt?.seconds || 0;
        const tB = b.createdAt?.seconds || 0;
        return tB - tA;
      });

      setLeads(items);
    } catch (err) {
      console.error('Error al cargar leads de ventas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const handleUpdateStatus = async (leadId: string, newStatus: 'new' | 'contacted' | 'converted' | 'discarded') => {
    setUpdatingId(leadId);
    try {
      await updateDoc(doc(db, 'sales_leads', leadId), {
        status: newStatus,
        updatedAt: new Date()
      });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err: any) {
      alert('Error al actualizar estado del prospecto: ' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteLead = async () => {
    if (!deletingLead) return;
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'sales_leads', deletingLead.id));
      setLeads(prev => prev.filter(l => l.id !== deletingLead.id));
      if (selectedLead?.id === deletingLead.id) {
        setSelectedLead(null);
      }
      setDeletingLead(null);
    } catch (err: any) {
      alert('Error al eliminar el prospecto: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper to detect requested plan from message text
  const detectPlan = (message: string = '') => {
    const lower = message.toLowerCase();
    if (lower.includes('constructora')) return 'constructora';
    if (lower.includes('estudio')) return 'estudio';
    if (lower.includes('inicio')) return 'inicio';
    if (lower.includes('enterprise')) return 'enterprise';
    return 'estudio'; // default suggestion
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = 
        (lead.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.message || '').toLowerCase().includes(searchTerm.toLowerCase());

      const currentStatus = lead.status || 'new';
      const matchesStatus = statusFilter === 'all' || currentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [leads, searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const total = leads.length;
    const nuevos = leads.filter(l => (!l.status || l.status === 'new')).length;
    const contactados = leads.filter(l => l.status === 'contacted').length;
    const convertidos = leads.filter(l => l.status === 'converted').length;
    return { total, nuevos, contactados, convertidos };
  }, [leads]);

  const formatDate = (ts: any) => {
    if (!ts) return 'Reciente';
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/80 backdrop-blur border border-slate-200/80 p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Leads</span>
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <Inbox size={20} />
            </div>
          </div>
          <p className="text-3xl font-extrabold text-slate-900 mt-2">{stats.total}</p>
        </div>

        <div className="bg-white/80 backdrop-blur border border-slate-200/80 p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Nuevos / Pendientes</span>
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
              <Clock size={20} />
            </div>
          </div>
          <p className="text-3xl font-extrabold text-amber-600 mt-2">{stats.nuevos}</p>
        </div>

        <div className="bg-white/80 backdrop-blur border border-slate-200/80 p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-cyan-600">En Contacto</span>
            <div className="p-2.5 bg-cyan-50 text-cyan-600 rounded-xl">
              <MessageSquare size={20} />
            </div>
          </div>
          <p className="text-3xl font-extrabold text-cyan-600 mt-2">{stats.contactados}</p>
        </div>

        <div className="bg-white/80 backdrop-blur border border-slate-200/80 p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Clientes Convertidos</span>
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 size={20} />
            </div>
          </div>
          <p className="text-3xl font-extrabold text-emerald-600 mt-2">{stats.convertidos}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por cliente, empresa o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500">Estado:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-xl px-3 py-2 font-medium focus:outline-none focus:border-cyan-500"
            >
              <option value="all">Todos los estados</option>
              <option value="new">Nuevos</option>
              <option value="contacted">En Contacto</option>
              <option value="converted">Convertidos</option>
              <option value="discarded">Descartados</option>
            </select>
          </div>

          <button
            onClick={loadLeads}
            disabled={loading}
            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors"
            title="Recargar prospectos"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
            <RefreshCw size={32} className="animate-spin text-cyan-600" />
            <p className="text-sm font-medium">Cargando bandeja de prospectos...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Inbox size={48} className="mx-auto mb-3 text-slate-300" />
            <p className="text-base font-semibold text-slate-700">No se encontraron prospectos</p>
            <p className="text-xs text-slate-500 mt-1">Los mensajes enviados desde la Landing Page aparecerán automáticamente aquí.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="py-3.5 px-5">Prospecto / Empresa</th>
                  <th className="py-3.5 px-4">Contacto</th>
                  <th className="py-3.5 px-4">Plan Interés</th>
                  <th className="py-3.5 px-4">Fecha</th>
                  <th className="py-3.5 px-4">Estado</th>
                  <th className="py-3.5 px-5 text-right">Acciones de Alta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredLeads.map((lead) => {
                  const detected = detectPlan(lead.message);
                  const st = lead.status || 'new';

                  return (
                    <tr key={lead.id} className="hover:bg-slate-50/60 transition-colors group">
                      {/* Name & Company */}
                      <td className="py-4 px-5">
                        <div className="font-bold text-slate-900">{lead.fullName || 'Sin nombre'}</div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                          <Building2 size={13} className="text-slate-400" />
                          <span>{lead.company || 'Empresa Independiente'}</span>
                          {lead.employeesRange && (
                            <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded font-mono text-slate-600">
                              {lead.employeesRange} emp
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5 text-xs text-slate-700">
                          <Mail size={13} className="text-indigo-500" />
                          <a href={`mailto:${lead.email}`} className="hover:underline font-mono">{lead.email}</a>
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1 font-mono">
                            <Phone size={13} className="text-emerald-500" />
                            <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="hover:underline">
                              {lead.phone}
                            </a>
                          </div>
                        )}
                      </td>

                      {/* Plan Interest */}
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold capitalize ${
                          detected === 'constructora' ? 'bg-orange-100 text-orange-700' :
                          detected === 'estudio' ? 'bg-indigo-100 text-indigo-700' :
                          detected === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                          'bg-cyan-100 text-cyan-700'
                        }`}>
                          <Sparkles size={12} />
                          {detected}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="py-4 px-4 text-xs text-slate-500 whitespace-nowrap">
                        {formatDate(lead.createdAt)}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4">
                        <select
                          value={st}
                          disabled={updatingId === lead.id}
                          onChange={(e) => handleUpdateStatus(lead.id, e.target.value as any)}
                          className={`text-xs font-bold rounded-lg px-2.5 py-1 border transition-colors cursor-pointer focus:outline-none ${
                            st === 'new' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            st === 'contacted' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                            st === 'converted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }`}
                        >
                          <option value="new">🟡 Nuevo</option>
                          <option value="contacted">🔵 Contactado</option>
                          <option value="converted">🟢 Convertido</option>
                          <option value="discarded">⚪ Descartado</option>
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedLead(lead)}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Ver mensaje completo"
                          >
                            <MessageSquare size={16} />
                          </button>

                          {lead.phone && (
                            <a
                              href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${lead.fullName}, te contacto de ArchiCore Analytic respecto a tu solicitud de información.`)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                              title="Enviar mensaje por WhatsApp"
                            >
                              <Phone size={13} />
                              WhatsApp
                            </a>
                          )}

                          <button
                            onClick={() => setDeletingLead(lead)}
                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            title="Eliminar contacto permanentemente"
                          >
                            <Trash2 size={16} />
                          </button>

                          <button
                            onClick={() => onProvisionRealClient({
                              company: lead.company || lead.fullName,
                              email: lead.email,
                              suggestedPlan: detected,
                              leadId: lead.id
                            })}
                            className="px-3 py-1.5 bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5"
                            title="Dar de alta como cliente real"
                          >
                            <UserPlus size={14} />
                            Dar de Alta
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal for Lead Message */}
      {selectedLead && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 border border-slate-200 shadow-2xl space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{selectedLead.fullName}</h3>
                <p className="text-xs text-slate-500">{selectedLead.company || 'Empresa no especificada'}</p>
              </div>
              <button 
                onClick={() => setSelectedLead(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="font-semibold text-slate-500">Correo:</span>
                <span className="font-mono font-bold text-slate-800">{selectedLead.email}</span>
              </div>
              {selectedLead.phone && (
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Teléfono:</span>
                  <span className="font-mono text-slate-800">{selectedLead.phone}</span>
                </div>
              )}
              {selectedLead.role && (
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">Cargo / Rol:</span>
                  <span className="text-slate-800">{selectedLead.role}</span>
                </div>
              )}
              {selectedLead.country && (
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-500">País:</span>
                  <span className="text-slate-800">{selectedLead.country}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="font-semibold text-slate-500">Fecha de Registro:</span>
                <span className="text-slate-800">{formatDate(selectedLead.createdAt)}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Mensaje del Cliente:</span>
              <div className="p-3 bg-slate-100/80 rounded-xl text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                {selectedLead.message || 'Sin mensaje adicional.'}
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-slate-100">
              <button
                onClick={() => setDeletingLead(selectedLead)}
                className="px-3 py-2 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                title="Eliminar contacto"
              >
                <Trash2 size={13} />
                Eliminar
              </button>

              <div className="flex gap-2">
                {selectedLead.phone && (
                  <a
                    href={`https://wa.me/${selectedLead.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${selectedLead.fullName}, te contacto de ArchiCore Analytic respecto a tu solicitud de información.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5"
                  >
                    💬 WhatsApp Directo
                  </a>
                )}
                <button
                  onClick={() => {
                    const lead = selectedLead;
                    setSelectedLead(null);
                    onProvisionRealClient({
                      company: lead.company || lead.fullName,
                      email: lead.email,
                      suggestedPlan: detectPlan(lead.message),
                      leadId: lead.id
                    });
                  }}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-xs font-bold transition-colors"
                >
                  Dar de Alta Cliente Real
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Confirmation Modal for Lead Deletion */}
      {deletingLead && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border border-slate-200 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="p-3 bg-rose-100 rounded-2xl border border-rose-200">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">¿Eliminar contacto?</h3>
                <p className="text-xs text-slate-500">Esta acción no se puede deshacer.</p>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-700 space-y-1">
              <p><strong className="text-slate-900">Contacto:</strong> {deletingLead.fullName}</p>
              <p><strong className="text-slate-900">Empresa:</strong> {deletingLead.company || 'Sin empresa'}</p>
              <p><strong className="text-slate-900">Correo:</strong> {deletingLead.email}</p>
            </div>

            <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 text-xs text-rose-800 flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-rose-600" />
              <span>
                <strong>Atención:</strong> El contacto será eliminado permanentemente de la base de datos de prospectos. Esta acción es <strong>irreversible</strong>.
              </span>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() => setDeletingLead(null)}
                disabled={isDeleting}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteLead}
                disabled={isDeleting}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 size={14} />
                {isDeleting ? 'Eliminando...' : 'Sí, eliminar contacto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
